<template>
  <div
    v-style:p="eventSimplified ? { lg: '1rem 0 0 4.25rem' } : undefined"
    :class="{ 'b-l m-2 pl-6': eventSimplified }"
  >
    <div
      v-for="(event, key) in sortedEvents"
      :key="event.id"
      class="<lg:px-4 <lg:py-8"
      :class="[
        { '<lg:b': !eventSimplified },
        !eventSimplified || !key ? 'mt-3' : 'mt-6',
        key ? 'lg:mt-11' : 'lg:mt-4',
      ]"
    >
      <!-- Extended events -->
      <base-collapse v-if="!eventSimplified" v-model="cardsOpen[event.id]" class-toggle="w-full">
        <!-- Collapsed -->
        <template #toggle="{ expanded }">
          <div class="w-full lg:grid" style="grid-template-columns: 30rem 1fr 8rem">
            <div class="flex between lg:grid <md:text-sm" style="grid-template-columns: 1fr 1fr">
              <!-- Left copy -->
              <div class="font-secondary uppercase">
                {{ event.eventType?.label }}
              </div>
              <!-- Date and time -->
              <div class="pl-4 font-secondary">
                <div>
                  {{ formatEventDateRange(event.startDate, event.endDate) }}
                </div>
                <div v-if="expanded" class="pt-1">
                  {{ formatTimeRange(event.startTime!, event.endTime!) }}
                </div>
              </div>
            </div>
            <!-- Title -->
            <div class="relative <lg:flex <lg:items-center <lg:between">
              <h3 v-html="event.titleHtml || event.title" class="text-xl fw-bold lh-6 <lg:my-6 lg:pl-8 lg:text-2xl" />
              <vf-icon class="lg:hidden" :dir="expanded ? 'up' : 'down'" name="chevron" size="lg" />
            </div>
            <!-- Toggle -->
            <div class="self-center text-right <lg:hidden">
              <span class="underscored fw-bold">
                {{ expanded ? $t.close : $t.more }}
              </span>
            </div>
          </div>
        </template>
        <!-- Expanded Content -->
        <div class="pt-2 lg:grid lg:pt-8" style="grid-template-columns: 30rem 1fr 8rem">
          <!-- Location -->
          <div class="w-full b-t b-brand-6 font-secondary lg:w-4/5 <lg:text-sm">
            <template v-if="event.locationName">
              <div class="flex pt-4 uppercase">
                <vf-icon class="mr-2" name="location" size="md" />
                {{ $t.event.location }}:
              </div>
              <p class="mt-2 b b-grey-50 rounded-xl p-3 uppercase lg:w-fit" style="padding-right:4rem">
                {{ event.locationName }}
              </p>
            </template>
          </div>
          <!-- Event copy -->
          <div class="lh-2 <lg:mt-6 lg:pl-8 lg:lh-4">
            <cms-rich-text :content="{ richText: event.bodyCopy }" />
            <div v-if="getEventAthletes(event).length" class="mt-4 py-4">
              <!-- NOTE: The text is in English in all locales -->
              <h2 class="mb-5 flex items-center gap-1 fw-bold font-secondary uppercase <lg:text-sm">
                <vf-icon class="mr-1" name="account" size="md" />Athletes
              </h2>
              <div class="flex gap-4 wrap">
                <component
                  :is="athlete.externalUrl ? BaseLink : 'div'"
                  v-for="(athlete, idx) in getEventAthletes(event)"
                  :key="`${athlete.title}-${idx}`"
                  v-style:w="{ md: '14.5rem' }"
                  class="h-14 w-full flex items-center overflow-hidden b p-2 between md:flex-none"
                  :href="athlete.externalUrl ? athlete.externalUrl : undefined"
                  rel="noopener noreferrer"
                  :style="{
                    'border-radius': '0.875rem',
                    'border-color': '#BABCBE',
                  }"
                  :target="athlete.externalUrl ? '_blank' : undefined"
                >
                  <div class="min-w-0 flex flex-auto items-center gap-3">
                    <img
                      v-if="athlete.canvasMediaObject"
                      :alt="athlete.canvasMediaObject.alt"
                      class="aspect-square rounded-lg"
                      :height="athlete.canvasMediaObject.height"
                      :src="athlete.canvasMediaObject.url"
                      :style="{ width: '2.44rem' }"
                      :width="athlete.canvasMediaObject.width"
                    >

                    <p v-if="athlete.title" class="line-clamp-2 subtitle-3">
                      {{ athlete.title }}
                    </p>
                  </div>
                  <vf-icon
                    :class="[isSocialMediaIcon(athlete.icon?.name) ? '-mr-4' : '']"
                    :dir="athlete.icon?.dir"
                    :name="athlete.icon?.icon || athlete.icon?.name"
                    size="5xl"
                    :src="athlete.icon?.src"
                  />
                </component>
              </div>
            </div>
          </div>
        </div>
      </base-collapse>
      <!-- Simplified events -->
      <div v-else class="space-y-4 lg:space-y-6">
        <div class="w-full gap-2 lg:grid" style="grid-template-columns: auto 1rem 1fr">
          <div class="relative text-md font-secondary">
            <div
              v-style:ml="{ lg: '-4.25rem' }"
              class="absolute box-content aspect-ratio-square w-4 b-8 b-white rounded-full bg-brand-6 -top-1 -ml-10 -translate-x-1/2"
            />
            {{ formatEventDateRange(event.startDate, event.endDate) }}
          </div>
          <div class="text-center <lg:hidden">
            |
          </div>
          <h3 v-html="event.titleHtml || event.title" class="text-2xl fw-bold lg:lh-none" />
        </div>
        <cms-rich-text :content="{ richText: event.bodyCopy }" />
        <div v-if="event.locationName" class="flex items-center text-xs font-secondary uppercase">
          <vf-icon class="mr-2" name="location" size="md" />
          {{ event.locationName }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { EventCollectionItem } from '#types/components/cms/event-collection-item'
import { BaseLink } from '#components'

interface EventWithAthletes extends EventCollectionItem {
  athletes?: Array<any>
}

const props = defineProps<{ events: EventCollectionItem[], eventSimplified?: boolean }>()
const cardsOpen = ref<Record<string, boolean>>({})

const sortedEvents = computed(() =>
  [...props.events].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
)

const formatEventDate = (date?: string) => date ? dateFormat(date, 'YYYY-MM-DD', 'DD/MM/YYYY') : ''

const formatEventDateRange = (startDate?: string, endDate?: string) => {
  const start = formatEventDate(startDate)
  if (!start) return ''

  if (endDate && endDate !== startDate) {
    return `${start} - ${formatEventDate(endDate)}`
  }

  return start
}

/**
 * Filters and returns athletes from an event, removing falsy values
 * @param event - The event object containing athletes
 * @returns An array of valid athlete objects
 */
const getEventAthletes = (event: EventWithAthletes) => (event.athletes || []).filter(Boolean)

/**
 * Checks if the icon name is a social media platform
 * @param iconName - The icon name to check
 * @returns True if the icon is a social media platform
 */
const isSocialMediaIcon = (iconName: string | undefined) => /^(?:youtube|instagram|tiktok|pinterest|facebook|x)$/.test(iconName || '')
</script>
